import mem from 'mem';
import { createSelector } from 'reselect';

const getDone = _ => _.done;
const getMarkers = _ => _.markers;
const getRegionId = (_, id) => id;
const getTags = (_, id, collectibles, tags) => tags;
const getCollectibles = (_, id, collectibles) => collectibles;

const selectRegionCollectibles = mem(
  (id, markers, collectibles) =>
    markers
      .filter(obj => obj.region === id)
      .filter(obj => collectibles.includes(obj.category)),
  { cacheKey: ([id]) => `select-region-collectibles::${id}` }
);

const getPercent = mem(
  (count, total) => (total === 0 ? 0 : Math.floor((count * 100) / total)),
  { cacheKey: ([count, total]) => `get-percent::${count}/${total}` }
);

export const getRegionScore = createSelector(
  getMarkers,
  getDone,
  getRegionId,
  getCollectibles,
  (markers, dones, id, collectibles) => {
    // selectionne uniquement les collectibles de la region
    const filtered = selectRegionCollectibles(id, markers, collectibles);

    // selectionne uniquement les collectibles effectués pour la région
    const checked = filtered.filter(obj => dones.includes(obj.id));

    const count = checked.length;
    const total = filtered.length;
    const percent = getPercent(count, total);
    const score = 100 - percent;
    const completed = percent === 100;
    return { completed, count, percent, score, total };
  }
);

export const getRegionScoreDetails = createSelector(
  getMarkers,
  getDone,
  getRegionId,
  getCollectibles,
  getTags,
  (markers, dones, id, collectibles, tags) => {
    // selectionne uniqument les `collectibles` de la region
    const filtered = selectRegionCollectibles(id, markers, collectibles);

    // groupe les `collectibles` de la region par `category`
    const categorized = filtered.reduce((acc, obj) => {
      const key = obj.category;
      const next = acc[key] ? [...acc[key], obj.id] : [obj.id];
      return { ...acc, [key]: next };
    }, {});

    // groupe les collectibles par tag
    // cree un nouvel objet pour chaque tag
    const tagged = Object.entries(categorized).reduce(
      (acc, [category, ids]) => {
        const tag = tags.find(obj => obj.items.includes(category));
        if (!tag) {
          throw new Error(`Cannot find tag for category ${category}`);
        }
        const previous = acc[tag.id] || [];
        return { ...acc, [tag.id]: [...previous, ...ids] };
      },
      {}
    );

    // calcul des `count`/`total` poour un tag
    const percentages = Object.entries(tagged).reduce((acc, [tagid, ids]) => {
      const checked = ids.filter(markerid => dones.includes(markerid));
      const total = ids.length;
      const count = checked.length;
      const completed = count === total;
      return { ...acc, [tagid]: { completed, count, total } };
    }, {});

    return percentages;
  }
);
